import { BaseEffect, EasingFunction, Easing, CanvasDimensions, EffectResult } from './BaseEffect';

export enum BorderWidthUnit {
    PX = 'px',
    PERCENT = '%'
}

/**
 * BorderEffect adds a customizable border to the container div.
 * This effect is applied to the container rather than the canvas.
 * 
 * @param color - Border color (CSS color string)
 * @param width - Border width value
 * @param unit - Border width unit ('px' or '%')
 * @param duration - Duration in seconds
 * @param at - Start time in seconds (optional)
 * @param easing - Easing function (optional)
 */
export class BorderEffect extends BaseEffect {
    color: string;
    width: number;
    unit: BorderWidthUnit;

    constructor(
        color: string = '#000000',
        width: number = 1,
        unit: BorderWidthUnit = BorderWidthUnit.PX,
        duration: number = 0,
        at?: number,
        easing: EasingFunction = Easing.linear
    ) {
        super('border', duration, at, easing, true);
        this.color = color;
        this.width = width;
        this.unit = unit;
    }

    apply(
        ctx: CanvasRenderingContext2D,
        dimensions: CanvasDimensions,
        progress: number,
        prev: EffectResult
    ): EffectResult {
        // This effect doesn't modify the canvas
        return prev;
    }

    getContainerStyle(
        progress: number, 
        dimensions?: CanvasDimensions, 
        effectResult?: EffectResult
    ): React.CSSProperties {
        let borderWidthCssString: string;
        let numericalBorderWidthPx: number;

        if (this.unit === BorderWidthUnit.PERCENT && dimensions) {
            const minDimension = Math.min(dimensions.resolutionWidth, dimensions.resolutionHeight);
            numericalBorderWidthPx = (this.width / 100) * minDimension;
            borderWidthCssString = `${numericalBorderWidthPx}px`;
        } else {
            numericalBorderWidthPx = this.width; // Assuming this.width is in px when unit is 'px'
            borderWidthCssString = `${this.width}px`;
        }

        // Default style
        let style: React.CSSProperties = {
            border: `${borderWidthCssString} solid ${this.color}`,
            boxSizing: 'border-box'
        };

        // If the shape is a circle, apply border-radius and size
        if (effectResult && effectResult.shape === 'circle' && effectResult.circle) {
            let { radius: contentRadius, x: contentCenterX, y: contentCenterY } = effectResult.circle;
            
            // For circles, we want to use the minimum dimension to ensure a perfect circle
            const minDimension = Math.min(dimensions?.resolutionWidth || 0, dimensions?.resolutionHeight || 0);
            const circleSize = minDimension;
            const circleRadius = circleSize / 2;

            style = {
                ...style,
                borderRadius: '50%',
                width: `${circleSize}px`,
                height: `${circleSize}px`,
                position: 'absolute',
                // Center the circle in the canvas
                left: `${(dimensions?.resolutionWidth || 0) / 2 - circleRadius}px`,
                top: `${(dimensions?.resolutionHeight || 0) / 2 - circleRadius}px`,
                overflow: 'hidden',
                pointerEvents: 'none',
            };
        }
        return style;
    }

    calculateProgress(currentTime: number): number {
        return 1; // Border is always visible
    }
}
